import React, { useState, useEffect, useCallback } from 'react';
import './DistrictOps.css';

// District override console: everything a customer's district admin can do to their own
// distribution, done on their behalf.
//
// This exists for the customers who can't or won't drive the district page themselves — you take
// the call, make the move, and they never have to log in. It hits admin twins of the same
// /district/... endpoints the customer's own portal uses, so a move made here is recorded in the
// district's history exactly like one they made, and their portal shows the new numbers straight
// away.
//
// One deliberate omission: you can nudge a school toward the new school year but you can't roll
// them over. Promote-or-wipe destroys a roster and depends on the school's grade structure, so it
// stays with someone who knows the school.

const TABS = [
  { key: 'distribution', label: 'Distribution' },
  { key: 'move', label: 'Move products' },
  { key: 'requests', label: 'Requests' },
  { key: 'rollover', label: 'New school year' },
  { key: 'history', label: 'History' },
];

function DistrictOps({ districtId, districtName }) {
  const [tab, setTab] = useState('distribution');
  const [distribution, setDistribution] = useState(null);
  const [mismatches, setMismatches] = useState([]);
  const [history, setHistory] = useState(null);
  const [rollover, setRollover] = useState(null);
  const [requests, setRequests] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);   // key of whatever action is in flight
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const api = process.env.REACT_APP_API_ENDPOINT;
  const call = useCallback(async (path, options = {}) => {
    const auth = 'Basic ' + btoa(
      `${localStorage.getItem('admin_username')}:${localStorage.getItem('admin_password')}`
    );
    const res = await fetch(`${api}/admin/districts/${districtId}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', 'Authorization': auth, ...(options.headers || {}) },
      cache: 'no-store',
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
  }, [api, districtId]);

  // Distribution + mismatches load together — the alert banner sits on the distribution view.
  const loadDistribution = useCallback(async () => {
    const [dist, mm] = await Promise.all([
      call('/distribution'),
      call('/allocation-mismatches'),
    ]);
    setDistribution(dist);
    setMismatches(mm || []);
  }, [call]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadDistribution()
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [loadDistribution]);

  // The other tabs load the first time you open them.
  useEffect(() => {
    const lazy = {
      history: [history, setHistory, '/history'],
      rollover: [rollover, setRollover, '/rollover-status'],
      requests: [requests, setRequests, '/total-requests'],
    }[tab];
    if (!lazy) return;
    const [current, set, path] = lazy;
    if (current !== null) return;
    call(path).then(set).catch(err => setError(err.message));
  }, [tab, history, rollover, requests, call]);

  const flash = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(null), 4000);
  };

  // ---- Bulk resets -------------------------------------------------------------------------
  // Three district-wide operations, deliberately separated by what they touch. The first changes
  // only paperwork; the second moves units but never touches a student; the third does both and
  // is the one that needs confirming. Each walks school × product, so they report progress and
  // collect failures rather than stopping dead or half-finishing in silence.
  const [bulkProgress, setBulkProgress] = useState('');

  // 1 — FIX THE NUMBERS, move nothing. Units reach a school by routes that don't update the
  // district's record (a school claiming its own order, a warehouse assignment, a scan), so the
  // record drifts. This sets every drifted record to the units actually on site. Moves nothing,
  // so it works with an empty pool.
  const onCorrectAllRecords = () => run('bulk-records', async () => {
    const rows = await call('/allocation-mismatches');
    if (!rows.length) { setBulkProgress('Every record already matches what is on site.'); return; }
    const failures = [];
    let fixed = 0;
    for (let i = 0; i < rows.length; i++) {
      setBulkProgress(`Correcting ${i + 1} of ${rows.length}…`);
      try {
        await call('/allocation-mismatches/fix', {
          method: 'POST',
          body: JSON.stringify({ schoolId: rows[i].schoolId, sku: rows[i].sku }),
        });
        fixed++;
      } catch (err) {
        failures.push(`${rows[i].schoolName} ${rows[i].sku}: ${err.message}`);
      }
    }
    await loadDistribution();
    setBulkProgress(`Corrected ${fixed} record${fixed === 1 ? '' : 's'}.`);
    if (failures.length) setError(`${failures.length} could not be corrected: ${failures.slice(0, 3).join('; ')}`);
  });

  // 2 — MOVE UNITS, touch nobody's assignment. Pulls back every unit a school holds with no
  // student on it. `inUse` is a count of PRODUCTS in use, so setting an allocation down to it can
  // only ever return spares — it never needs pool stock and never strands a student.
  const onRecallSpares = () => run('bulk-spares', async () => {
    const dist = await call('/distribution');
    const work = (dist.schools || []).flatMap(school =>
      (school.allocations || [])
        .filter(a => (a.inUse ?? 0) < a.amount)
        .map(a => ({ schoolId: school.schoolId, schoolName: school.schoolName, sku: a.sku, target: a.inUse ?? 0 }))
    );
    if (!work.length) { setBulkProgress('No spare units to recall — every allocated product has a student on it.'); return; }
    const failures = [];
    let changed = 0;
    for (let i = 0; i < work.length; i++) {
      const w = work[i];
      setBulkProgress(`Recalling spares (${i + 1} of ${work.length})…`);
      try {
        await call('/distribution', {
          method: 'POST',
          body: JSON.stringify({ schoolId: w.schoolId, sku: w.sku, amount: w.target }),
        });
        changed++;
      } catch (err) {
        // One school's refusal shouldn't strand the rest of the run.
        failures.push(`${w.schoolName} ${w.sku}: ${err.message}`);
      }
    }
    await loadDistribution();
    setBulkProgress(`Recalled spares from ${changed} allocation${changed === 1 ? '' : 's'}.`);
    if (failures.length) setError(`${failures.length} could not be recalled: ${failures.slice(0, 3).join('; ')}`);
  });

  // 3 — UNASSIGN EVERYONE, then pull everything back. Unassign has to happen first: an allocation
  // can never drop below what is assigned, so the units are pinned until the students let go.
  const onRecallEverything = () => run('bulk-recall', async () => {
    const dist = await call('/distribution');
    const schools = dist.schools || [];
    for (let i = 0; i < schools.length; i++) {
      setBulkProgress(`Unassigning at ${schools[i].schoolName} (${i + 1} of ${schools.length})…`);
      await call(`/schools/${schools[i].schoolId}/mass-unassign`, { method: 'POST' });
    }
    const work = schools.flatMap(school =>
      (school.allocations || []).filter(a => a.amount > 0).map(a => ({ schoolId: school.schoolId, sku: a.sku }))
    );
    for (let i = 0; i < work.length; i++) {
      setBulkProgress(`Returning products to the pool (${i + 1} of ${work.length})…`);
      await call('/distribution', {
        method: 'POST',
        body: JSON.stringify({ schoolId: work[i].schoolId, sku: work[i].sku, amount: 0 }),
      });
    }
    await loadDistribution();
    setBulkProgress('Everything is back in the district pool.');
  });

  const run = async (key, fn) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div className="dops-loading">Loading district distribution…</div>;

  return (
    <div className="dops">
      <div className="dops-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`dops-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.key === 'requests' && requests && requests.length > 0 && (
              <span className="dops-tab-dot">{requests.length}</span>
            )}
          </button>
        ))}
      </div>

      <p className="dops-note">
        You're acting as {districtName}'s district admin. Changes here are real and show up in
        their portal immediately, logged in the district's history like any move they'd make.
      </p>

      {error && <div className="dops-error">{error}</div>}
      {notice && <div className="dops-notice">{notice}</div>}

      {tab === 'distribution' && (
        <DistributionTab
          distribution={distribution}
          mismatches={mismatches}
          busy={busy}
          onSetAllocation={(schoolId, sku, amount) => run(`alloc-${schoolId}-${sku}`, async () => {
            setDistribution(await call('/distribution', {
              method: 'POST',
              body: JSON.stringify({ schoolId, sku, amount }),
            }));
            setMismatches(await call('/allocation-mismatches'));
            flash('Allocation updated.');
          })}
          onFixMismatch={(schoolId, sku) => run(`fix-${schoolId}-${sku}`, async () => {
            setMismatches(await call('/allocation-mismatches/fix', {
              method: 'POST',
              body: JSON.stringify({ schoolId, sku }),
            }));
            setDistribution(await call('/distribution'));
            flash('Record corrected — no products moved.');
          })}
          progress={bulkProgress}
          onCorrectAllRecords={onCorrectAllRecords}
          onRecallSpares={onRecallSpares}
          onRecallEverything={onRecallEverything}
        />
      )}

      {tab === 'move' && (
        <MoveTab
          distribution={distribution}
          busy={busy}
          onMove={(payload) => run('move', async () => {
            setDistribution(await call('/redistribute', {
              method: 'POST',
              body: JSON.stringify(payload),
            }));
            setMismatches(await call('/allocation-mismatches'));
            flash(`Moved ${payload.amount} × ${payload.sku}.`);
          })}
        />
      )}

      {tab === 'requests' && (
        <RequestsTab
          requests={requests}
          busy={busy}
          onDecide={(id, decision) => run(`req-${id}`, async () => {
            const result = await call(`/total-requests/${id}/${decision}`, { method: 'POST' });
            setRequests(await call('/total-requests'));
            setDistribution(await call('/distribution'));
            if (result.warnings && result.warnings.length) {
              flash(`Approved, but: ${result.warnings.join('; ')}`);
            } else {
              flash(decision === 'approve' ? 'Request approved and the school was emailed.' : 'Request declined and the school was emailed.');
            }
          })}
        />
      )}

      {tab === 'rollover' && (
        <RolloverTab
          rollover={rollover}
          busy={busy}
          onNudge={(schoolId) => run(`nudge-${schoolId || 'all'}`, async () => {
            const result = await call('/nudge-rollover', {
              method: 'POST',
              body: JSON.stringify({ schoolId: schoolId ?? null }),
            });
            flash(result.nudged === 0
              ? 'Nothing sent — those schools have no admin accounts with an email on file.'
              : `Reminder sent to ${result.nudged} school${result.nudged === 1 ? '' : 's'}.`);
          })}
        />
      )}

      {tab === 'history' && <HistoryTab history={history} />}
    </div>
  );
}

// ---- Distribution: the pool, and each school's per-product allocation -----------------------

function DistributionTab({
  distribution, mismatches, busy, onSetAllocation, onFixMismatch,
  progress, onCorrectAllRecords, onRecallSpares, onRecallEverything,
}) {
  const [confirmRecall, setConfirmRecall] = useState(false);
  // Draft values per school+SKU, so a half-typed number doesn't fire a save.
  const [drafts, setDrafts] = useState({});
  const key = (schoolId, sku) => `${schoolId}::${sku}`;

  if (!distribution) return null;
  const { pool = [], schools = [] } = distribution;
  const skus = pool.map(p => p.sku);
  const nameFor = (sku) => pool.find(p => p.sku === sku)?.displayName || sku;
  const poolRemaining = (sku) => pool.find(p => p.sku === sku)?.remaining ?? 0;

  return (
    <div className="dops-panel">
      {/* District-wide resets. Ordered by how much they disturb: paperwork, then spare stock, then
          everything. The destructive one is styled apart and asks first. */}
      <div className="dops-bulk">
        <button className="dops-btn small" disabled={!!busy} onClick={onCorrectAllRecords}>
          {busy === 'bulk-records' ? 'Working…' : "Correct records to what's on site"}
        </button>
        <button className="dops-btn small" disabled={!!busy} onClick={onRecallSpares}>
          {busy === 'bulk-spares' ? 'Working…' : 'Recall extra units'}
        </button>
        <button className="dops-btn small danger" disabled={!!busy} onClick={() => setConfirmRecall(true)}>
          {busy === 'bulk-recall' ? 'Working…' : 'Recall everything to pool'}
        </button>
      </div>
      {progress ? <p className="dops-progress">{progress}</p> : null}

      {confirmRecall && (
        <div className="dops-confirm">
          <strong>Recall every product to the district pool?</strong>
          <p>
            This unassigns every product from every student at all {schools.length} schools, then pulls
            every product back into the pool. Students keep their records, but nobody will have a
            product until you allocate and assign again. This can't be undone in one step.
          </p>
          <div className="dops-confirm-row">
            <button className="dops-btn small ghost" onClick={() => setConfirmRecall(false)}>Cancel</button>
            <button
              className="dops-btn small danger"
              onClick={() => { setConfirmRecall(false); onRecallEverything(); }}
            >
              Yes, recall everything
            </button>
          </div>
        </div>
      )}

      {mismatches.length > 0 && (
        <div className="dops-alert">
          <strong>{mismatches.length} record{mismatches.length === 1 ? '' : 's'} out of step.</strong>{' '}
          The district's recorded allocation disagrees with the units actually at the school —
          usually stock that arrived without the record being updated. Correcting sets the record to
          what's really there; no products move.
          <table className="dops-table dops-alert-table">
            <thead>
              <tr><th>School</th><th>Product</th><th>On site</th><th>Recorded</th><th></th></tr>
            </thead>
            <tbody>
              {mismatches.map(m => (
                <tr key={`${m.schoolId}-${m.sku}`}>
                  <td>{m.schoolName}</td>
                  <td>{nameFor(m.sku)}</td>
                  <td>{m.atSchool}</td>
                  <td className="dops-over">{m.allocated}</td>
                  <td>
                    <button
                      className="dops-btn small"
                      disabled={!!busy}
                      onClick={() => onFixMismatch(m.schoolId, m.sku)}
                    >
                      {busy === `fix-${m.schoolId}-${m.sku}` ? 'Fixing…' : `Correct to ${m.atSchool}`}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 className="dops-h3">District pool</h3>
      {pool.length === 0 ? (
        <p className="dops-empty">This district doesn't own any products yet — nothing has been claimed to it.</p>
      ) : (
        <table className="dops-table">
          <thead>
            <tr><th>Product</th><th>Owned</th><th>At schools</th><th>Unallocated</th></tr>
          </thead>
          <tbody>
            {pool.map(p => (
              <tr key={p.sku}>
                <td>{p.displayName}<span className="dops-sku">{p.sku}</span></td>
                <td>{p.claimed}</td>
                <td>{p.allocated}</td>
                <td className={p.remaining > 0 ? 'dops-avail' : ''}>{p.remaining}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3 className="dops-h3">Per-school allocation</h3>
      {schools.length === 0 ? (
        <p className="dops-empty">No schools in this district yet.</p>
      ) : (
        schools.map(school => {
          const byS = Object.fromEntries((school.allocations || []).map(a => [a.sku, a]));
          // Every district product the school could hold — including ones it has none of yet, so
          // you can allocate from scratch. A product the school doesn't hold AND the pool can't
          // supply is unactionable, so leave it out rather than print a row of zeroes.
          const rows = skus
            .map(sku => byS[sku] || { sku, amount: 0, inUse: 0 })
            .filter(a => a.amount > 0 || poolRemaining(a.sku) > 0);
          return (
            <div className="dops-school" key={school.schoolId}>
              <h4 className="dops-school-name">{school.schoolName}</h4>
              {rows.length === 0 ? (
                <p className="dops-empty">
                  Nothing allocated, and the pool has nothing spare to give this school.
                </p>
              ) : (
                <table className="dops-table">
                  <thead>
                    <tr><th>Product</th><th>In use</th><th>Allocated</th><th></th></tr>
                  </thead>
                  <tbody>
                    {rows.map(alloc => {
                      const sku = alloc.sku;
                      const k = key(school.schoolId, sku);
                      const draft = drafts[k];
                      const value = draft === undefined ? String(alloc.amount) : draft;
                      const parsed = parseInt(value, 10);
                      const changed = !Number.isNaN(parsed) && parsed !== alloc.amount;
                      // The pool caps how far up you can go; students already assigned cap how far down.
                      const max = alloc.amount + poolRemaining(sku);
                      const invalid = changed && (parsed < alloc.inUse || parsed > max);
                      return (
                        <tr key={k}>
                          <td>{nameFor(sku)}</td>
                          <td>{alloc.inUse}</td>
                          <td>
                            <input
                              className={`dops-num ${invalid ? 'invalid' : ''}`}
                              type="number"
                              min={alloc.inUse}
                              max={max}
                              value={value}
                              onChange={e => setDrafts(d => ({ ...d, [k]: e.target.value }))}
                            />
                            <span className="dops-range">of {max}</span>
                          </td>
                          <td>
                            {changed && (
                              <>
                                <button
                                  className="dops-btn small"
                                  disabled={invalid || busy === `alloc-${school.schoolId}-${sku}`}
                                  title={
                                    invalid
                                      ? (parsed < alloc.inUse
                                        ? `${alloc.inUse} are assigned to students — you can't go below that.`
                                        : `The pool only has ${poolRemaining(sku)} spare.`)
                                      : ''
                                  }
                                  onClick={() => {
                                    onSetAllocation(school.schoolId, sku, parsed);
                                    setDrafts(d => { const n = { ...d }; delete n[k]; return n; });
                                  }}
                                >
                                  {busy === `alloc-${school.schoolId}-${sku}` ? 'Saving…' : 'Save'}
                                </button>
                                <button
                                  className="dops-btn small ghost"
                                  onClick={() => setDrafts(d => { const n = { ...d }; delete n[k]; return n; })}
                                >
                                  Cancel
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

// ---- Move: school → school, or to/from the pool ---------------------------------------------

function MoveTab({ distribution, busy, onMove }) {
  const [from, setFrom] = useState('pool');
  const [to, setTo] = useState('');
  const [sku, setSku] = useState('');
  const [amount, setAmount] = useState('1');

  if (!distribution) return null;
  const { pool = [], schools = [] } = distribution;

  const parseTarget = (v) => (v === 'pool' ? null : Number(v));
  const available = () => {
    if (!sku) return 0;
    if (from === 'pool') return pool.find(p => p.sku === sku)?.remaining ?? 0;
    const school = schools.find(s => String(s.schoolId) === from);
    const alloc = school?.allocations?.find(a => a.sku === sku);
    // Units assigned to a student stay put — only the spare ones are movable.
    return alloc ? Math.max(0, alloc.amount - alloc.inUse) : 0;
  };

  const avail = available();
  const n = parseInt(amount, 10);
  const valid = sku && to !== '' && from !== to && !Number.isNaN(n) && n > 0 && n <= avail;

  return (
    <div className="dops-panel">
      <p className="dops-lead">
        Move products anywhere inside the district — one school to another, out of the pool, or back
        into it. Units already assigned to a student never move; only spares are available.
      </p>

      <div className="dops-move">
        <label>
          From
          <select value={from} onChange={e => setFrom(e.target.value)}>
            <option value="pool">District pool</option>
            {schools.map(s => <option key={s.schoolId} value={String(s.schoolId)}>{s.schoolName}</option>)}
          </select>
        </label>

        <span className="dops-arrow">→</span>

        <label>
          To
          <select value={to} onChange={e => setTo(e.target.value)}>
            <option value="">Choose…</option>
            <option value="pool">District pool</option>
            {schools.map(s => <option key={s.schoolId} value={String(s.schoolId)}>{s.schoolName}</option>)}
          </select>
        </label>

        <label>
          Product
          <select value={sku} onChange={e => setSku(e.target.value)}>
            <option value="">Choose…</option>
            {pool.map(p => <option key={p.sku} value={p.sku}>{p.displayName}</option>)}
          </select>
        </label>

        <label>
          Quantity
          <input type="number" min="1" max={avail || 1} value={amount} onChange={e => setAmount(e.target.value)} />
        </label>

        <button className="dops-btn" disabled={!valid || busy === 'move'} onClick={() => onMove({
          fromSchoolId: parseTarget(from),
          toSchoolId: parseTarget(to),
          sku,
          amount: n,
        })}>
          {busy === 'move' ? 'Moving…' : 'Move'}
        </button>
      </div>

      {sku && (
        <p className={`dops-avail-note ${avail === 0 ? 'zero' : ''}`}>
          {avail} spare {pool.find(p => p.sku === sku)?.displayName || sku} available to move from{' '}
          {from === 'pool' ? 'the pool' : schools.find(s => String(s.schoolId) === from)?.schoolName}.
        </p>
      )}
      {from === to && to !== '' && <p className="dops-avail-note zero">Pick two different places.</p>}
    </div>
  );
}

// ---- Requests: schools asking their district for more product -------------------------------

function RequestsTab({ requests, busy, onDecide }) {
  if (requests === null) return <div className="dops-loading">Loading requests…</div>;
  if (requests.length === 0) {
    return <p className="dops-empty">No pending product-total requests from this district's schools.</p>;
  }
  return (
    <div className="dops-panel">
      <p className="dops-lead">
        Schools ask their district for a new product total here. Approving allocates from the pool
        and emails the school; declining just emails them. Either way it's sent as the district.
      </p>
      {requests.map(req => (
        <div className="dops-request" key={req.id}>
          <div className="dops-request-head">
            <strong>{req.schoolName}</strong>
            <span className="dops-request-date">{new Date(req.createdAt).toLocaleDateString()}</span>
          </div>
          <table className="dops-table compact">
            <thead><tr><th>Product</th><th>Requested total</th></tr></thead>
            <tbody>
              {req.items.map(i => (
                <tr key={i.sku}><td>{i.sku}</td><td><strong>{i.requestedTotal}</strong></td></tr>
              ))}
            </tbody>
          </table>
          {req.note ? <p className="dops-request-note">“{req.note}”</p> : null}
          <div className="dops-request-actions">
            <button
              className="dops-btn"
              disabled={busy === `req-${req.id}`}
              onClick={() => onDecide(req.id, 'approve')}
            >
              {busy === `req-${req.id}` ? 'Working…' : 'Approve'}
            </button>
            <button
              className="dops-btn ghost"
              disabled={busy === `req-${req.id}`}
              onClick={() => onDecide(req.id, 'reject')}
            >
              Decline
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- Rollover: who's started the new school year --------------------------------------------

function RolloverTab({ rollover, busy, onNudge }) {
  if (rollover === null) return <div className="dops-loading">Loading rollover status…</div>;
  const pending = rollover.schools.filter(s => !s.rolledOver);
  return (
    <div className="dops-panel">
      <p className="dops-lead">
        {rollover.rolledOverCount} of {rollover.totalSchools} schools have started the {rollover.currentSchoolYear}–
        {String(rollover.currentSchoolYear + 1).slice(-2)} school year. You can send a reminder, but the
        rollover itself stays with the school — promote-or-wipe rewrites their roster and depends on
        their grade structure.
      </p>
      {pending.length > 0 && (
        <button className="dops-btn" disabled={busy === 'nudge-all'} onClick={() => onNudge(null)}>
          {busy === 'nudge-all' ? 'Sending…' : `Remind all ${pending.length} school${pending.length === 1 ? '' : 's'}`}
        </button>
      )}
      <table className="dops-table">
        <thead><tr><th>School</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {rollover.schools.map(s => (
            <tr key={s.schoolId}>
              <td>{s.schoolName}</td>
              <td>
                <span className={`dops-pill ${s.rolledOver ? 'ok' : 'wait'}`}>
                  {s.rolledOver ? 'Rolled over' : 'Not started'}
                </span>
              </td>
              <td>
                {!s.rolledOver && (
                  <button
                    className="dops-btn small ghost"
                    disabled={busy === `nudge-${s.schoolId}`}
                    onClick={() => onNudge(s.schoolId)}
                  >
                    {busy === `nudge-${s.schoolId}` ? 'Sending…' : 'Remind'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---- History: claims + every distribution move ----------------------------------------------

function HistoryTab({ history }) {
  if (history === null) return <div className="dops-loading">Loading history…</div>;
  if (history.length === 0) return <p className="dops-empty">No claims or product moves recorded for this district yet.</p>;
  return (
    <div className="dops-panel">
      <table className="dops-table">
        <thead><tr><th>Date</th><th>What</th><th>Product</th><th>Qty</th></tr></thead>
        <tbody>
          {history.map((h, i) => (
            <tr key={i}>
              <td className="nowrap">{h.date ? new Date(h.date).toLocaleDateString() : '-'}</td>
              <td>
                <span className={`dops-pill ${h.kind === 'claim' ? 'claim' : 'move'}`}>{h.kind}</span>
                {h.description}
              </td>
              <td>{h.product || '-'}</td>
              <td>{h.amount ?? '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default DistrictOps;
